import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { environment } from 'src/environments/environment';

export type StatusTone = 'good' | 'warn' | 'bad';

export type AddressTaxStatus =
  | 'suggested'
  | 'dismissed'
  | 'pending'
  | 'registered'
  | 'action_needed';

// The statuses a tax edit can set: adopt a suggestion, dismiss it, or register.
export type AddressTaxEditStatus = 'pending' | 'dismissed' | 'registered';

export type TaxFilingStatus = 'upcoming' | 'filed' | 'overdue';

export type ComplianceCheckStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export interface TaxFiling {
  id: number;
  address_tax_id: number;
  period_start: string;
  period_end: string;
  due_date: string;
  calculated_amount: number;
  platform_collected_amount: number;
  remitted_amount: number;
  stays_count: number;
  unpriced_stays_count: number;
  status: TaxFilingStatus;
  filed_on: string | null;
  confirmation_number: string | null;
  filed_by: string | null;
}

export interface AddressTax {
  id: number;
  address_id: number;
  name: string;
  rate: number | null;
  category: string | null;
  agency: string | null;
  jurisdiction: string | null;
  portal_url: string | null;
  registration_url: string | null;
  form_number: string | null;
  frequency: string | null;
  due_rule: string | null;
  collecting_platforms: CollectingPlatform[];
  host_must_file_even_if_platform_collects: boolean | null;
  license_number: string | null;
  registered_on: string | null;
  status: AddressTaxStatus;
  source_key: string | null;
  source_compliance_check_id: number | null;
  created_at: string;
  next_filing: TaxFiling | null;
}

export interface TaxComplianceCheck {
  id: number;
  address_id: number;
  status: ComplianceCheckStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxFieldsPayload {
  name?: string;
  category?: string;
  rate?: number;
  agency?: string;
  frequency?: TaxFrequency;
  // One of dueRuleItems(); the API refuses other text and can't clear it.
  due_rule?: string;
  portal_url?: string;
  collecting_platforms?: CollectingPlatform[];
}

export interface AddTaxPayload extends TaxFieldsPayload {
  address_id: number;
  name: string;
}

export interface UpdateTaxPayload extends TaxFieldsPayload {
  status?: AddressTaxEditStatus;
  license_number?: string;
}

export interface MarkFiledPayload {
  filed_on?: string;
  remitted_amount?: number;
  confirmation_number?: string;
  filed_by?: string;
}

export type TaxFrequency = 'monthly' | 'quarterly' | 'semi-annually' | 'annually';

export type CollectingPlatform = 'airbnb' | 'vrbo' | 'booking_com';

// The values match what the API stores and answers, so an edited tax shows its
// own frequency selected.
export const FREQUENCY_ITEMS: { value: TaxFrequency; viewValue: string }[] = [
  { value: 'monthly', viewValue: 'Monthly' },
  { value: 'quarterly', viewValue: 'Quarterly' },
  { value: 'semi-annually', viewValue: 'Semi-annually' },
  { value: 'annually', viewValue: 'Annually' },
];

export const PLATFORM_LABEL: Record<CollectingPlatform, string> = {
  airbnb: 'Airbnb',
  vrbo: 'Vrbo',
  booking_com: 'Booking.com',
};

export const TAX_STATUS_LABEL: Record<AddressTaxStatus, string> = {
  suggested: 'Suggested',
  dismissed: 'Dismissed',
  pending: 'Registration needed',
  registered: 'Registered',
  action_needed: 'Action needed',
};

export const TAX_STATUS_TONE: Record<AddressTaxStatus, StatusTone> = {
  suggested: 'warn',
  dismissed: 'warn',
  pending: 'warn',
  registered: 'good',
  action_needed: 'warn',
};

export const FILING_STATUS_LABEL: Record<TaxFilingStatus, string> = {
  upcoming: 'Upcoming',
  filed: 'Filed',
  overdue: 'Overdue',
};

export const FILING_STATUS_TONE: Record<TaxFilingStatus, StatusTone> = {
  upcoming: 'warn',
  filed: 'good',
  overdue: 'bad',
};

// Status colours pulled from the shared design tokens, matching the Compliance
// page (the sibling surface): text/foreground and the tinted chip background.
export const TONE_COLOR: Record<StatusTone, string> = {
  good: 'var(--tidy-success)',
  warn: 'var(--tidy-warning)',
  bad: 'var(--tidy-error)',
};

export const TONE_BG: Record<StatusTone, string> = {
  good: 'var(--tidy-success-bg)',
  warn: 'var(--tidy-warning-bg)',
  bad: 'var(--tidy-error-bg)',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Taxes with a category the marketplaces can collect for the host.
export const COLLECTED_CATEGORIES = ['occupancy', 'lodging', 'sales', 'tourism'];

// The report's categories are free text, so a tax a platform already collects
// counts too, whatever its category.
export function isPlatformCollectable(tax: AddressTax): boolean {
  return COLLECTED_CATEGORIES.includes((tax.category || '').toLowerCase()) || (tax.collecting_platforms || []).length > 0;
}

// Backend statuses that represent a tax adopted onto the property (i.e. not a
// pending suggestion or a dismissed one).
export const ACTIVE_TAX_STATUSES: AddressTaxStatus[] = ['pending', 'registered', 'action_needed'];

// The API has no overdue tax status: a tax is overdue when its next filing is.
export function isTaxOverdue(tax: AddressTax): boolean {
  return tax.next_filing?.status === 'overdue';
}

export function taxStatusLabel(tax: AddressTax): string {
  return isTaxOverdue(tax) ? 'Overdue' : TAX_STATUS_LABEL[tax.status];
}

export function taxStatusTone(tax: AddressTax): StatusTone {
  return isTaxOverdue(tax) ? 'bad' : TAX_STATUS_TONE[tax.status];
}

export function taxPortalUrl(tax: AddressTax | null | undefined): string {
  return tax?.portal_url || tax?.registration_url || '';
}

// Lines built here skip the template's translate pipe, so they are translated as
// they are built. Values go in %placeholders% so each language can place them.
export function translateTax(text: string, values: Record<string, string | number> = {}): string {
  return Object.entries(values).reduce(
    (line, [key, value]) => line.split(`%${key}%`).join(String(value)),
    new TranslationPipe().transform(text),
  );
}

// Filing deadlines the API reads, spelled its way ("last day of" or "Nth of the
// following month"). Without one a filing is due 20 days after its period ends.
export function dueRuleItems(): { value: string; viewValue: string }[] {
  return [
    { value: 'last day of the following month', viewValue: 'Last day of the next month' },
    ...Array.from({ length: 28 }, (_, index) => ({
      value: `${ordinal(index + 1)} of the following month`,
      viewValue: translateTax('Day %day% of the next month', { day: index + 1 }),
    })),
  ];
}

// The report spells deadlines in its own casing ("Last day of the following month").
export function dueRuleOptionValue(dueRule: string): string {
  const lower = dueRule.trim().toLowerCase();
  return dueRuleItems().find(item => item.value === lower)?.value ?? dueRule;
}

function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) { return `${day}th`; }
  return `${day}${({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[day % 10] ?? 'th'}`;
}

export function frequencyLabel(frequency: string | null | undefined): string {
  if (!frequency) { return ''; }
  const item = FREQUENCY_ITEMS.find(option => option.value === frequency);
  return translateTax(item?.viewValue || capitalize(frequency));
}

// Stays without rent count as $0, so the owed amount is only an estimate when
// some of them are unpriced.
export function filingEstimateNote(filing: TaxFiling): string {
  if (!filing.unpriced_stays_count) { return ''; }
  const priced = filing.stays_count - filing.unpriced_stays_count;
  return translateTax('Estimate from %priced% of %total% stays', { priced, total: filing.stays_count });
}

export function formatShortDate(iso: string | null): string {
  if (!iso) { return ''; }
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) { return ''; }
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

export function formatMonthDay(iso: string | null): string {
  if (!iso) { return ''; }
  const [, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!month || !day) { return ''; }
  return `${MONTHS[month - 1]} ${day}`;
}

export function formatCents(cents: number | null | undefined): string {
  const value = (cents || 0) / 100;
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function capitalize(value: string | null | undefined): string {
  if (!value) { return ''; }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

@Injectable({
  providedIn: 'root'
})
export class TaxesService {

  private http = inject(HttpClient);

  async getAddressTaxes(addressIds: number[] = [], statuses: AddressTaxStatus[] = []): Promise<AddressTax[]> {
    let params = new HttpParams();
    if (addressIds.length > 0) { params = params.set('address_ids', addressIds.join(',')); }
    if (statuses.length > 0) { params = params.set('statuses', statuses.join(',')); }
    return firstValueFrom(
      this.http.get<AddressTax[]>(`${environment.api_url}api/v1/customer/address-taxes`, { params })
    );
  }

  async getTax(addressId: number, taxId: number): Promise<AddressTax | null> {
    const taxes = await this.getAddressTaxes([addressId]);
    return taxes.find(tax => tax.id === taxId) || null;
  }

  // Checks come newest first; null means the property was never checked.
  async getLatestComplianceCheck(addressId: number): Promise<TaxComplianceCheck | null> {
    const params = new HttpParams().set('address_ids', String(addressId));
    const checks = await firstValueFrom(
      this.http.get<TaxComplianceCheck[]>(`${environment.api_url}api/v1/customer/compliance-checks`, { params })
    );
    return checks?.[0] || null;
  }

  async addTax(payload: AddTaxPayload): Promise<AddressTax> {
    return firstValueFrom(
      this.http.post<AddressTax>(`${environment.api_url}api/v1/customer/address-taxes`, payload)
    );
  }

  async updateTax(taxId: number, payload: UpdateTaxPayload): Promise<AddressTax> {
    return firstValueFrom(
      this.http.put<AddressTax>(`${environment.api_url}api/v1/customer/address-taxes/${taxId}`, payload)
    );
  }

  async deleteTax(taxId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.api_url}api/v1/customer/address-taxes/${taxId}`)
    );
  }

  async getFilings(taxId: number): Promise<TaxFiling[]> {
    const params = new HttpParams().set('address_tax_ids', String(taxId));
    return firstValueFrom(
      this.http.get<TaxFiling[]>(`${environment.api_url}api/v1/customer/address-tax-filings`, { params })
    );
  }

  async markFiled(filingId: number, payload: MarkFiledPayload): Promise<TaxFiling> {
    return firstValueFrom(
      this.http.post<TaxFiling>(`${environment.api_url}api/v1/customer/address-tax-filings/${filingId}/mark-filed`, payload)
    );
  }
}
